import { useState, useEffect, useCallback, useMemo } from 'react';

import { Loader2, AlertCircle, Copy, Check, BookOpen, Bot, Hash } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ConnectorIcon } from '@/components/connectors/ConnectorIcon';
import { Connection, ConnectionUsageInstructionsResponse, ConnectorListItem } from '@optidev/shared-types';
import { connectionsApi } from '@/lib/api/connections';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

// Code block with syntax highlighting and copy button
function CodeBlockWithCopy({
  children,
  language
}: {
  children: string;
  language?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [children]);

  const languageMap: Record<string, string> = {
    js: 'javascript',
    ts: 'typescript',
    tsx: 'tsx',
    jsx: 'jsx',
    sh: 'bash',
    shell: 'bash',
  };
  const normalizedLanguage = language ? (languageMap[language] || language) : 'javascript';

  return (
    <div className="group relative my-3 rounded-lg overflow-hidden border border-border/50">
      <div className="flex items-center justify-between px-4 py-1.5 bg-zinc-800 dark:bg-zinc-800 border-b border-border/30">
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">
          {language || 'code'}
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                className={cn(
                  "h-6 px-2 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700",
                  copied && "text-emerald-400 hover:text-emerald-400"
                )}
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {copied ? 'Copied!' : 'Copy code'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <SyntaxHighlighter
        language={normalizedLanguage}
        style={oneDark}
        customStyle={{
          margin: 0,
          padding: '0.75rem 1rem',
          fontSize: '0.8125rem',
          lineHeight: '1.5',
          borderRadius: 0,
          background: '#1e1e1e',
        }}
        codeTagProps={{
          style: {
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          }
        }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
}

// Shared markdown component overrides used inside each accordion section
const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-xl font-bold text-foreground border-b border-border/50 pb-2 mb-4 mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-base font-semibold text-foreground mt-5 mb-3">
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-sm font-semibold text-foreground mt-4 mb-2">
      {children}
    </h3>
  ),
  h4: ({ children }: { children?: React.ReactNode }) => (
    <h4 className="text-sm font-medium text-foreground mt-3 mb-1.5">
      {children}
    </h4>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="text-sm text-foreground/90 leading-relaxed mb-3">
      {children}
    </p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc list-inside text-sm text-foreground/90 mb-3 space-y-1">
      {children}
    </ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal list-inside text-sm text-foreground/90 mb-3 space-y-1">
      {children}
    </ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-foreground/90">{children}</li>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      className="text-primary hover:underline text-sm"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  hr: () => <hr className="border-border/50 my-5" />,
  code: ({ node, className, children, ...props }: {
    node?: { position?: { start: { line: number }; end: { line: number } } };
    className?: string;
    children?: React.ReactNode;
  }) => {
    const match = /language-(\w+)/.exec(className || '');
    const codeString = String(children).replace(/\n$/, '');
    const isCodeBlock = match || (node?.position?.start.line !== node?.position?.end.line);

    if (isCodeBlock) {
      return (
        <CodeBlockWithCopy language={match?.[1]}>
          {codeString}
        </CodeBlockWithCopy>
      );
    }

    return (
      <code
        className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono text-foreground"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
};

interface MarkdownSection {
  title: string;
  content: string;
}

// Parse markdown into sections split by ## headings.
// The h1 title (if any) is stripped since the modal header shows the connection name.
function parseMarkdownSections(markdown: string): { intro: string; sections: MarkdownSection[] } {
  const lines = markdown.split('\n');
  let intro = '';
  const sections: MarkdownSection[] = [];
  let currentSection: MarkdownSection | null = null;
  let skipFirstH1 = true;

  for (const line of lines) {
    // Skip the first h1 since the modal header already shows the name
    if (skipFirstH1 && /^# /.test(line)) {
      skipFirstH1 = false;
      continue;
    }

    if (/^## /.test(line)) {
      // Save previous section
      if (currentSection) {
        sections.push({ ...currentSection, content: currentSection.content.trim() });
      }
      currentSection = {
        title: line.replace(/^## /, '').trim(),
        content: '',
      };
    } else if (currentSection) {
      currentSection.content += line + '\n';
    } else {
      // Content before the first ## heading -> intro text
      intro += line + '\n';
    }
  }

  // Push the last section
  if (currentSection) {
    sections.push({ ...currentSection, content: currentSection.content.trim() });
  }

  return { intro: intro.trim(), sections };
}

interface ConnectionUsageInstructionsModalProps {
  connection: Connection | null;
  connector?: ConnectorListItem | null;
  isOpen: boolean;
  onClose: () => void;
}

// Parse connection name to extract display name and account email
const parseConnectionName = (name: string): { displayName: string; account: string | null } => {
  const emailMatch = name.match(/\(([^)]+@[^)]+)\)/);
  if (emailMatch) {
    const account = emailMatch[1];
    const displayName = name.replace(emailMatch[0], '').replace(/\s+/g, ' ').trim();
    return { displayName, account };
  }
  return { displayName: name, account: null };
};

export function ConnectionUsageInstructionsModal({
  connection,
  connector,
  isOpen,
  onClose,
}: ConnectionUsageInstructionsModalProps) {
  const { getIdToken } = useAuth();
  const [instructions, setInstructions] = useState<ConnectionUsageInstructionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState(false);

  const handleCopyConnectionId = async (): Promise<void> => {
    if (!connection) return;
    try {
      await navigator.clipboard.writeText(connection.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Fetch instructions when modal opens
  useEffect(() => {
    if (!isOpen || !connection) {
      setInstructions(null);
      setError(null);
      return;
    }

    const fetchInstructions = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const token = await getIdToken();
        if (!token) {
          setError('Authentication required');
          return;
        }

        const data = await connectionsApi.getUsageInstructions(token, connection.id);
        setInstructions(data);
      } catch (err) {
        console.error('Failed to fetch usage instructions:', err);
        setError(err instanceof Error ? err.message : 'Failed to load instructions');
      } finally {
        setLoading(false);
      }
    };

    fetchInstructions();
  }, [isOpen, connection, getIdToken]);

  // Parse the markdown into collapsible sections
  const { intro, sections } = useMemo(() => {
    if (!instructions?.markdown) return { intro: '', sections: [] };
    return parseMarkdownSections(instructions.markdown);
  }, [instructions?.markdown]);

  if (!connection) return null;

  const { displayName, account } = parseConnectionName(connection.name);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden p-0 gap-0">
        {/* Header */}
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border/50">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-2 rounded-lg bg-muted/50 border border-border/50">
              <ConnectorIcon
                connector={connector || { icon: 'Database', name: connection.type || 'Unknown' }}
                className="h-5 w-5 text-foreground"
              />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-semibold flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                How to Use
              </DialogTitle>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="text-sm font-medium text-foreground">{displayName}</span>
                {account && (
                  <span className="text-xs text-muted-foreground">({account})</span>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 space-y-3">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading instructions...</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="p-3 rounded-full bg-destructive/10">
                <AlertCircle className="h-7 w-7 text-destructive" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">Failed to load instructions</p>
                <p className="text-sm text-muted-foreground mt-1">{error}</p>
              </div>
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          )}

          {!loading && !error && instructions && (
            <div className="px-6 py-5 space-y-4">
              {/* Agent awareness banner */}
              <div className="flex items-start gap-3 rounded-lg bg-primary/5 border border-primary/15 px-4 py-3">
                <Bot className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-sm text-foreground/80 leading-relaxed">
                  <span className="font-medium text-foreground">The AI agent already knows how to use this connection.</span>
                  {' '}The information below is for your reference only - you can simply ask the agent and it will handle authentication and API calls automatically.
                </div>
              </div>

              {/* Connection ID - compact inline */}
              <div className="flex items-center gap-2 rounded-lg bg-muted/40 border border-border/50 px-3 py-2">
                <Hash className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground font-medium">Connection ID</span>
                <code className="text-xs font-mono text-foreground/80 select-all truncate flex-1">
                  {connection.id}
                </code>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyConnectionId}
                        className={cn(
                          "h-6 w-6 p-0 flex-shrink-0",
                          copiedId
                            ? "text-emerald-500"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {copiedId ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {copiedId ? 'Copied!' : 'Copy Connection ID'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Intro text (content before first ## heading, if any) */}
              {intro && (
                <div className="text-sm text-foreground/80 leading-relaxed">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {intro}
                  </ReactMarkdown>
                </div>
              )}

              {/* Collapsible sections */}
              {sections.length > 0 && (
                <Accordion type="multiple" className="w-full">
                  {sections.map((section, index) => (
                    <AccordionItem
                      key={index}
                      value={`section-${index}`}
                      className="border border-border/50 rounded-lg mb-2 last:mb-0 overflow-hidden px-0 data-[state=open]:bg-muted/20"
                    >
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 transition-colors text-sm font-medium text-foreground">
                        {section.title}
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4 pt-0">
                        <div className="border-t border-border/30 pt-3">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={markdownComponents}
                          >
                            {section.content}
                          </ReactMarkdown>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}

              {/* Fallback: if no sections were parsed, render raw markdown */}
              {sections.length === 0 && (
                <div className="max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {instructions.markdown}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ConnectionUsageInstructionsModal;
